import React from 'react';
import { motion } from 'framer-motion';
import { Star, Target, TrendingUp, CheckCircle2, AlertCircle, BookOpen, BarChart2, Clock, Zap, Sparkles, Play, ChevronLeft, Info, Lightbulb, Activity, Award, Brain } from 'lucide-react';
import type { AssessmentReport } from '../types';
import type { ModuleId } from '../../content';
import { Button } from '../../components/ui/Button';
import { Card } from '../../components/ui/Card';
const MAX_TIER = 16;
const MIN_QUESTIONS_FOR_ESTIMATE = 3;
interface AssessmentReportPageProps {
  report: AssessmentReport;
  onExit: () => void;
  onRetake?: () => void;
  onPracticeWeakAreas?: () => void;
  onStartPracticeForModule?: (moduleId: ModuleId) => void;
}
const stagger = (i: number) => ({
  delay: 0.06 * i
});
const fadeUp = {
  initial: {
    opacity: 0,
    y: 16
  },
  animate: {
    opacity: 1,
    y: 0
  },
  transition: {
    duration: 0.35,
    ease: 'easeOut'
  }
};
export function AssessmentReportPage({
  report,
  onExit,
  onRetake,
  onPracticeWeakAreas,
  onStartPracticeForModule
}: AssessmentReportPageProps) {
  const {
    score,
    time,
    byTier,
    estimatedLevels,
    overallEstimatedLevelCode,
    overallExpectedLevelCode,
    overallEstimatedTier,
    overallExpectedTier,
    overallCitoLevel,
    growthIndicators,
    actionPlan,
    strengthsBySkill,
    focusAreasBySkill,
    strongAreas,
    weakAreas,
    suggestedPractice,
    recommendation,
    dokDistribution
  } = report;
  const formatDate = (ts: number) => new Date(ts).toLocaleDateString('nl-NL', {
    day: 'numeric',
    month: 'long',
    year: 'numeric'
  });
  const formatTime = (seconds: number) => {
    const m = Math.floor(seconds / 60);
    const s = Math.round(seconds % 60);
    return m > 0 ? `${m}m ${s}s` : `${s}s`;
  };
  const hasAnalysis = strengthsBySkill.length > 0 || focusAreasBySkill.length > 0 || strongAreas.length > 0 || weakAreas.length > 0;
  const hasActionPlan = actionPlan.beginMet || actionPlan.gaVerderMet.length > 0 || actionPlan.uitdaging;
  const hasTierData = byTier.length > 0;
  // R3-DOK1 surfacing: only show the DOK card if at least one classified
  // item was answered (i.e. not everything ended up in the `unknown` bucket).
  const dokLevels = dokDistribution ? ([{
    level: 1,
    label: 'DOK 1 · Onthouden',
    count: dokDistribution.counts.dok1,
    correct: dokDistribution.correctCounts.dok1,
    accuracy: dokDistribution.accuracy.dok1,
    tone: 'bg-slate-400'
  }, {
    level: 2,
    label: 'DOK 2 · Toepassen',
    count: dokDistribution.counts.dok2,
    correct: dokDistribution.correctCounts.dok2,
    accuracy: dokDistribution.accuracy.dok2,
    tone: 'bg-indigo-400'
  }, {
    level: 3,
    label: 'DOK 3 · Analyseren',
    count: dokDistribution.counts.dok3,
    correct: dokDistribution.correctCounts.dok3,
    accuracy: dokDistribution.accuracy.dok3,
    tone: 'bg-violet-500'
  }, {
    level: 4,
    label: 'DOK 4 · Creëren',
    count: dokDistribution.counts.dok4,
    correct: dokDistribution.correctCounts.dok4,
    accuracy: dokDistribution.accuracy.dok4,
    tone: 'bg-fuchsia-500'
  }] as const).filter(d => d.count > 0) : [];
  const hasDokData = dokLevels.length > 0;
  const dokTotal = dokLevels.reduce((sum, d) => sum + d.count, 0);
  const dokUnknown = dokDistribution?.counts.unknown ?? 0;
  return <div className="min-h-screen bg-slate-50 font-sans flex flex-col" data-id="element-819" data-mp="31rl77h1mvskt">
      {/* Minimal Header */}
      <header className="sticky top-0 z-20 bg-white/90 backdrop-blur-md border-b border-slate-200/80 px-4 py-2.5 flex items-center justify-between" data-id="element-820" data-mp="2wryrn0yc208u">
        <button onClick={onExit} className="flex items-center gap-1.5 text-slate-500 hover:text-slate-900 font-medium transition-colors text-sm" aria-label="Terug naar dashboard" data-id="element-821" data-mp="2rtow2p8p4xmp">
          <ChevronLeft className="w-5 h-5" data-id="element-822" data-mp="10r6ki54ge6f1" />
          <span className="hidden sm:inline" data-id="element-823" data-mp="m0z743tazdl7">Terug</span>
        </button>
        <h1 className="text-sm font-bold text-slate-700 tracking-tight" data-id="element-824" data-mp="qzafea3y9g1b">
          Leerlingprofiel
        </h1>
        <div className="w-14" data-id="element-825" data-mp="cyzqvdq44djb" />
      </header>

      {/* Scrollable Content */}
      <main className="flex-1 w-full max-w-3xl mx-auto px-4 sm:px-6 py-5 sm:py-8 space-y-6" data-id="element-826" data-mp="hym2xju7fnqj">
        {/* 1. Overall Result Hero */}
        <motion.section {...fadeUp} className="relative overflow-hidden rounded-2xl bg-slate-800 text-white shadow-lg" data-id="element-827" data-mp="324vh3p4yt99">
          <div className="absolute -top-8 -right-8 opacity-[0.06]" data-id="element-828" data-mp="82islqp78isi">
            <Award className="w-56 h-56" data-id="element-829" data-mp="1xgnuuxedsgn2" />
          </div>

          <div className="relative px-5 pt-5 pb-4 sm:px-7 sm:pt-7 sm:pb-5 flex flex-col sm:flex-row items-center gap-5" data-id="element-830" data-mp="22e78pw762reh">
            {/* Left: Level + Context */}
            <div className="flex-1 text-center sm:text-left" data-id="element-831" data-mp="3muf4lciw92f5">
              <p className="text-slate-400 text-sm font-medium mb-1" data-id="element-832" data-mp="3hyg8f2rbqg46">
                Groep {report.selectedGroup} · {formatDate(report.completedAt)}
              </p>
              <div className="flex items-baseline justify-center sm:justify-start gap-2.5 mb-1.5" data-id="element-833" data-mp="3rvunoz1igyuw">
                <h2 className="text-4xl sm:text-5xl font-black tracking-tight leading-none" data-id="element-834" data-mp="9hbposj744y4">
                  {overallEstimatedLevelCode}
                </h2>
                <span className="text-slate-400 font-medium text-base" data-id="element-835" data-mp="5fqinntcekvu">
                  / {overallExpectedLevelCode}
                </span>
                {overallCitoLevel && <span className={`ml-2 px-2.5 py-0.5 rounded-full text-xs font-bold border ${overallCitoLevel.bgColor} ${overallCitoLevel.color} ${overallCitoLevel.borderColor}`} data-id="element-836" data-mp="eh7u2svt1ye7">
                    {overallCitoLevel.label}
                  </span>}
              </div>
              <p className="text-slate-300 text-sm sm:text-base font-medium max-w-sm leading-snug" data-id="element-837" data-mp="k6zl3l4xpmyb">
                {overallEstimatedTier === overallExpectedTier ? 'Je zit precies op het verwachte niveau. Keurig gewerkt!' : overallEstimatedTier > overallExpectedTier ? 'Uitstekend! Je scoort boven het verwachte niveau.' : 'Je bent goed op weg, maar er is nog ruimte voor groei.'}
              </p>
            </div>

            {/* Right: Score Ring */}
            <div className="w-24 h-24 sm:w-28 sm:h-28 relative flex-shrink-0" data-id="element-838" data-mp="1ct29l648smkz">
              <svg className="w-full h-full transform -rotate-90" data-id="element-839" data-mp="1gv5yawfx9no7">
                <circle cx="50%" cy="50%" r="42%" fill="none" stroke="rgba(255,255,255,0.15)" strokeWidth="7" data-id="element-840" data-mp="1gue2plmbrpxx" />
                <motion.circle cx="50%" cy="50%" r="42%" fill="none" stroke="white" strokeWidth="7" strokeLinecap="round" initial={{
                pathLength: 0
              }} animate={{
                pathLength: score.percentage / 100
              }} transition={{
                duration: 1.2,
                ease: 'easeOut'
              }} data-id="element-841" data-mp="20xa667zmhy6h" />
              </svg>
              <div className="absolute inset-0 flex flex-col items-center justify-center" data-id="element-842" data-mp="29xqafjo6x9ce">
                <span className="text-2xl sm:text-3xl font-black leading-none" data-id="element-843" data-mp="25wd4l9l3l65h">
                  {score.percentage}%
                </span>
                <span className="text-[9px] font-bold text-slate-400 uppercase tracking-widest mt-0.5" data-id="element-844" data-mp="32faz6bn3htq6">
                  Score
                </span>
              </div>
            </div>
          </div>

          {/* Stats Strip */}
          <div className="bg-slate-900/40 px-5 py-3 grid grid-cols-3 text-center text-sm divide-x divide-slate-600/30" data-id="element-845" data-mp="3haa04ny3xv46">
            <div data-id="element-846" data-mp="37df6ojgoykwr">
              <span className="text-slate-400 text-[10px] uppercase font-bold tracking-wider block mb-0.5" data-id="element-847" data-mp="37dxbw5lc09gt">
                Goed
              </span>
              <span className="font-bold" data-id="element-848" data-mp="3ccrsr1zqnnya">
                {score.correctCount}/{score.totalQuestions}
              </span>
            </div>
            <div data-id="element-849" data-mp="8vhx0k89li80">
              <span className="text-slate-400 text-[10px] uppercase font-bold tracking-wider block mb-0.5" data-id="element-850" data-mp="8w1j8d4y6sru">
                Tijd
              </span>
              <span className="font-bold" data-id="element-851" data-mp="3q5zisp868bdv">{formatTime(time.totalSeconds)}</span>
            </div>
            <div data-id="element-852" data-mp="3v52qyijg6xq2">
              <span className="text-slate-400 text-[10px] uppercase font-bold tracking-wider block mb-0.5" data-id="element-853" data-mp="2eoq1a88lrtf7">
                Per vraag
              </span>
              <span className="font-bold" data-id="element-854" data-mp="2jom9yks8w913">
                {Math.round(time.avgPerQuestion)}s
              </span>
            </div>
          </div>
        </motion.section>

        {/* 2. Level per Subject */}
        <motion.section {...fadeUp} transition={stagger(1)} className="space-y-3" data-id="element-855" data-mp="wqlfvhyjfbdp">
          <h3 className="flex items-center gap-2 text-lg font-bold text-slate-900 px-0.5" data-id="element-856" data-mp="1aojqcc9qmnnx">
            <BarChart2 className="w-4.5 h-4.5 text-slate-500" data-id="element-857" data-mp="16n6ftovp9wao" />
            Niveau per Vakgebied
          </h3>
          <Card className="overflow-hidden shadow-sm" data-id="element-858" data-mp="ixc9lq5vz5ze">
            <div className="divide-y divide-slate-100" data-id="element-859" data-mp="e1wt0qghuvza">
              {estimatedLevels.map(level => {
              const growth = growthIndicators.find(g => g.moduleId === level.moduleId);
              const isUnknown = level.questionCount < MIN_QUESTIONS_FOR_ESTIMATE;
              return <div key={level.moduleId} className="px-4 py-3.5 sm:px-5" data-id="element-860" data-mp="3rfrww2faasxm">
                    <div className="flex items-center gap-3 mb-2" data-id="element-861" data-mp="3rdp3d8exaho5">
                      <span className="w-28 sm:w-36 font-bold text-slate-900 text-sm truncate" data-id="element-862" data-mp="1gvfu1b2yu5pe">
                        {level.moduleTitle}
                      </span>
                      <div className="flex-1 h-3 bg-slate-100 rounded-full overflow-hidden relative" data-id="element-863" data-mp="17uhxteyrr2hd">
                        {level.confidenceBand && !isUnknown && <div className="absolute top-0 bottom-0 bg-slate-200/50 z-0" style={{
                      left: `${level.confidenceBand.low / MAX_TIER * 100}%`,
                      width: `${(level.confidenceBand.high - level.confidenceBand.low) / MAX_TIER * 100}%`
                    }} data-id="element-864" data-mp="xx3kvpc20vtt" />}
                        <div className="absolute top-0 bottom-0 w-px bg-slate-400 z-10" style={{
                      left: `${level.expectedTier / MAX_TIER * 100}%`
                    }} data-id="element-865" data-mp="f5aodkxs0shc" />
                        <motion.div initial={{
                      width: 0
                    }} animate={{
                      width: `${level.estimatedTier / MAX_TIER * 100}%`
                    }} transition={{
                      duration: 0.8,
                      ease: 'easeOut'
                    }} className={`h-full rounded-full ${isUnknown ? 'bg-slate-300' : level.delta > 0 ? 'bg-emerald-500' : level.delta === 0 ? 'bg-blue-500' : level.delta >= -1 ? 'bg-yellow-400' : 'bg-amber-500'}`} data-id="element-866" data-mp="2qt6286fxbiyw" />
                      </div>
                      <div className="w-20 flex flex-col items-end flex-shrink-0" data-id="element-867" data-mp="1kd3tnru8i68h">
                        <span className="font-black text-slate-800 text-sm" data-id="element-868" data-mp="1oep0owk37qar">
                          {isUnknown ? '?' : level.estimatedLevelCode}
                        </span>
                        {!isUnknown && level.citoLevel && <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded mt-0.5 ${level.citoLevel.bgColor} ${level.citoLevel.color}`} data-id="element-869" data-mp="2mr1ev55y3oma">
                            {level.citoLevel.label}
                          </span>}
                      </div>
                    </div>
                    <div className="flex justify-between items-center text-xs pl-0 sm:pl-[7.5rem] sm:ml-3" data-id="element-870" data-mp="23tgy6ldc3wcr">
                      <span className="flex items-center gap-1.5 text-slate-500" data-id="element-871" data-mp="2hsztgdl1rd7n">
                        {growth && <span className={`w-1.5 h-1.5 rounded-full inline-block ${['groei_snel', 'gaat_goed', 'ik_groei'].includes(growth.level) ? 'bg-emerald-500' : ['let_op', 'extra_aandacht'].includes(growth.level) ? 'bg-amber-500' : ['krimpt', 'actie_vereist'].includes(growth.level) ? 'bg-red-500' : 'bg-slate-300'}`} data-id="element-872" data-mp="2dpbpe4vmizr5" />}
                        {growth?.label || 'Te weinig data'}
                      </span>
                      <span className="text-slate-400 flex items-center gap-2" data-id="element-873" data-mp="3msy3hx6ro84i">
                        {level.sem !== undefined && !isUnknown && <span className="text-slate-400/80" title="Standaardmeetfout (SEM)" data-id="element-874" data-mp="j9v325o8ps8v">
                            ±{level.sem.toFixed(1)}
                          </span>}
                        <span data-id="element-875" data-mp="ejepmlkf66mi">
                          {level.accuracy}% · {level.questionCount} vr.
                        </span>
                      </span>
                    </div>
                  </div>;
            })}
            </div>
            <div className="bg-slate-50 px-4 py-2.5 border-t border-slate-100 text-xs text-slate-400 flex items-start gap-1.5" data-id="element-876" data-mp="171a9a9j50tpe">
              <Info className="w-3.5 h-3.5 mt-px flex-shrink-0" data-id="element-877" data-mp="3ahypjmipxw2a" />
              <span data-id="element-878" data-mp="3agnk7i9mkg92">
                Streepje = verwacht niveau. Balk = gemeten niveau. Lichte zone =
                betrouwbaarheidsmarge (±1 SEM).
              </span>
            </div>
          </Card>
        </motion.section>

        {/* 3. Tier Breakdown & Time (side by side) */}
        {(hasTierData || time.totalSeconds > 0) && <motion.section {...fadeUp} transition={stagger(2)} className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-id="element-879" data-mp="3t8q6b951coqp">
            {hasTierData && <Card className="p-4 shadow-sm" data-id="element-880" data-mp="3jin20csgrod2">
                <h4 className="flex items-center gap-2 font-bold text-slate-900 text-sm mb-3" data-id="element-881" data-mp="3jhvjo3nvnn4q">
                  <Activity className="w-4 h-4 text-slate-500" data-id="element-882" data-mp="kteumfbfmzt6" />
                  Moeilijkheidsgraad
                </h4>
                <div className="space-y-2.5" data-id="element-883" data-mp="g0lq9sjvlthm">
                  {byTier.map(tier => <div key={tier.tierRange} data-id="element-884" data-mp="oc59u7ukpgcv">
                      <div className="flex justify-between text-xs mb-1" data-id="element-885" data-mp="sbw4imn80gmo">
                        <span className="font-medium text-slate-600" data-id="element-886" data-mp="sdz683xqjy78">
                          Tier {tier.tierRange}
                        </span>
                        <span className="text-slate-400" data-id="element-887" data-mp="12bda742h2uin">
                          {tier.accuracy}% ({tier.correctCount}/
                          {tier.totalQuestions})
                        </span>
                      </div>
                      <div className="h-1.5 bg-slate-100 rounded-full overflow-hidden" data-id="element-888" data-mp="1b976if08lo5t">
                        <div className="h-full bg-indigo-400 rounded-full transition-all" style={{
                  width: `${tier.accuracy}%`
                }} data-id="element-889" data-mp="1g2a2suwefbpp" />
                      </div>
                    </div>)}
                </div>
              </Card>}

            <Card className="p-4 shadow-sm" data-id="element-890" data-mp="15bn84onthoe3">
              <h4 className="flex items-center gap-2 font-bold text-slate-900 text-sm mb-3" data-id="element-891" data-mp="10bshmm7yf0tk">
                <Clock className="w-4 h-4 text-slate-500" data-id="element-892" data-mp="109xwb6srt6oy" />
                Tijdsbesteding
              </h4>
              <div className="space-y-4" data-id="element-893" data-mp="1a9ykb61l4azh">
                <div className="flex items-center justify-between" data-id="element-894" data-mp="1a8x3sm1suwad">
                  <span className="text-slate-600 text-sm" data-id="element-895" data-mp="1o13dpqed071d">Totale tijd</span>
                  <span className="font-bold text-slate-900" data-id="element-896" data-mp="1o36biwlr74gn">
                    {formatTime(time.totalSeconds)}
                  </span>
                </div>
                <div className="flex items-center justify-between" data-id="element-897" data-mp="1xy7vzf533icw">
                  <span className="text-slate-600 text-sm" data-id="element-898" data-mp="1szwm54esh7uo">Per vraag</span>
                  <span className="font-bold text-slate-900" data-id="element-899" data-mp="3li6d9lr0vbb">
                    {Math.round(time.avgPerQuestion)}s
                  </span>
                </div>
                <div className="flex items-center justify-between" data-id="element-900" data-mp="3utdxai24oqu6">
                  <span className="text-slate-600 text-sm" data-id="element-901" data-mp="2a9ijz0y8djv5">Tempo</span>
                  <span className={`font-semibold px-2.5 py-0.5 rounded-full text-xs ${time.avgPerQuestion < 20 ? 'bg-emerald-100 text-emerald-700' : time.avgPerQuestion > 50 ? 'bg-amber-100 text-amber-700' : 'bg-blue-50 text-blue-700'}`} data-id="element-902" data-mp="2f8lnggykbi10">
                    {time.avgPerQuestion < 20 ? 'Vlot' : time.avgPerQuestion > 50 ? 'Rustig' : 'Gemiddeld'}
                  </span>
                </div>
              </div>
            </Card>
          </motion.section>}

        {/* 3b. DOK / Denkdiepte breakdown (R3-DOK1 surfacing) */}
        {hasDokData && <motion.section {...fadeUp} transition={stagger(2)} data-id="element-903" data-mp="3bz4djr0gb2x3">
            <Card className="p-4 shadow-sm" data-id="element-904" data-mp="3bznnoljv0n0k">
              <div className="flex items-start justify-between gap-2 mb-3" data-id="element-905" data-mp="3gxyt8k34gbcz">
                <h4 className="flex items-center gap-2 font-bold text-slate-900 text-sm" data-id="element-906" data-mp="3gvuu0fg640j1">
                  <Brain className="w-4 h-4 text-slate-500" data-id="element-907" data-mp="23tgnqjizuxs6" />
                  Denkdiepte
                </h4>
                <span className="text-[10px] text-slate-400 font-medium" title="DOK = Depth of Knowledge (Webb). 1=onthouden, 2=toepassen, 3=analyseren, 4=creëren." data-id="element-908" data-mp="1zs4yif418eki">
                  {dokTotal} vragen geclassificeerd
                  {dokUnknown > 0 ? ` · ${dokUnknown} zonder DOK` : ''}
                </span>
              </div>
              <div className="space-y-2.5" data-id="element-909" data-mp="1g3w561qr62jf">
                {dokLevels.map(d => {
              const sharePct = dokTotal > 0 ? d.count / dokTotal * 100 : 0;
              return <div key={d.level} data-id="element-910" data-mp="1736d994m4waz">
                      <div className="flex justify-between text-xs mb-1" data-id="element-911" data-mp="36er0hmqkrcgq">
                        <span className="font-medium text-slate-600" data-id="element-912" data-mp="36buz1ofiq5nj">
                          {d.label}
                        </span>
                        <span className="text-slate-400" data-id="element-913" data-mp="38zf7b9wcwfe6">
                          {d.accuracy !== null ? `${d.accuracy}%` : '—'} (
                          {d.correct}/{d.count})
                        </span>
                      </div>
                      <div className="h-1.5 bg-slate-100 rounded-full overflow-hidden" data-id="element-914" data-mp="3ip8gk4910if9">
                        <motion.div initial={{
                    width: 0
                  }} animate={{
                    width: `${sharePct}%`
                  }} transition={{
                    duration: 0.6,
                    ease: 'easeOut'
                  }} className={`h-full rounded-full ${d.tone}`} data-id="element-915" data-mp="3nn07auxxpyhv" />
                      </div>
                    </div>;
            })}
              </div>
              <p className="mt-3 text-[10px] text-slate-400 leading-relaxed" data-id="element-916" data-mp="jbek6ql0ar5d">
                Hoger DOK = meer denkwerk. Een goede toets dekt meerdere
                niveaus.
              </p>
            </Card>
          </motion.section>}

        {/* 4. Strengths & Focus Areas */}
        {hasAnalysis && <motion.section {...fadeUp} transition={stagger(3)} className="space-y-3" data-id="element-917" data-mp="xyfgy45q3j5n">
            <h3 className="flex items-center gap-2 text-lg font-bold text-slate-900 px-0.5" data-id="element-918" data-mp="197w6ycxhe2s">
              <Target className="w-4.5 h-4.5 text-slate-500" data-id="element-919" data-mp="58zxqr3wmjyn" />
              Inhoudelijke Analyse
            </h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-id="element-920" data-mp="a832eafz2o63">
              {/* Strengths */}
              <Card className="p-4 shadow-sm border-emerald-100/60 bg-emerald-50/20" data-id="element-921" data-mp="f8qs8ssgsyyq">
                <h4 className="flex items-center gap-2 font-bold text-emerald-800 text-sm mb-3" data-id="element-922" data-mp="1lmyf3qv26fk0">
                  <Star className="w-4 h-4 text-emerald-500 fill-emerald-500" data-id="element-923" data-mp="1qmbaizzzwuy7" />
                  Sterke Punten
                </h4>
                {strongAreas.length > 0 && <p className="text-xs text-emerald-600 mb-3 font-medium" data-id="element-924" data-mp="366bw6bcan8b4">
                    {strongAreas.join(', ')}
                  </p>}
                {strengthsBySkill.length > 0 ? <ul className="space-y-2" data-id="element-925" data-mp="2wh0tb6g02zvo">
                    {strengthsBySkill.slice(0, 3).flatMap(area => area.skills.slice(0, 2).map(skill => <li key={`${area.moduleId}-${skill.skillName}`} className="flex items-start gap-2" data-id="element-926" data-mp="2nfua2plyvayq">
                          <CheckCircle2 className="w-3.5 h-3.5 text-emerald-500 mt-0.5 flex-shrink-0" data-id="element-927" data-mp="2dhehei7ajtn4" />
                          <div className="min-w-0" data-id="element-928" data-mp="gp4ipgx1hy0m">
                            <span className="font-semibold text-emerald-900 text-xs capitalize" data-id="element-929" data-mp="gntejfzomo8z">
                              {skill.skillName}
                            </span>
                            <span className="text-emerald-500 text-xs ml-1.5" data-id="element-930" data-mp="26576930d92ne">
                              {Math.round(skill.avgAccuracy)}%
                            </span>
                          </div>
                        </li>))}
                  </ul> : <p className="text-emerald-500/70 text-xs italic" data-id="element-931" data-mp="217os8y7hrx8j">
                    Nog geen specifieke sterke punten.
                  </p>}
              </Card>

              {/* Focus Areas */}
              <Card className="p-4 shadow-sm border-amber-100/60 bg-amber-50/20" data-id="element-932" data-mp="2ttqclczuv5cu">
                <h4 className="flex items-center gap-2 font-bold text-amber-800 text-sm mb-3" data-id="element-933" data-mp="2fvidozj9dtz1">
                  <AlertCircle className="w-4 h-4 text-amber-500" data-id="element-934" data-mp="2jw27bt4mifgf" />
                  Aandachtspunten
                </h4>
                {weakAreas.length > 0 && <p className="text-xs text-amber-600 mb-3 font-medium" data-id="element-935" data-mp="18iq9byvjor26">
                    {weakAreas.join(', ')}
                  </p>}
                {focusAreasBySkill.length > 0 ? <ul className="space-y-2" data-id="element-936" data-mp="vyuemulekb3d">
                    {focusAreasBySkill.slice(0, 3).flatMap(area => area.skills.slice(0, 2).map(skill => <li key={`${area.moduleId}-${skill.skillName}`} className="flex items-start gap-2" data-id="element-937" data-mp="15xilqybun5sa">
                          <TrendingUp className="w-3.5 h-3.5 text-amber-500 mt-0.5 flex-shrink-0" data-id="element-938" data-mp="d9mo9regh9ug" />
                          <div className="min-w-0" data-id="element-939" data-mp="ry0r42bvgyb0">
                            <span className="font-semibold text-amber-900 text-xs capitalize" data-id="element-940" data-mp="rzu19rlp20nr">
                              {skill.skillName}
                            </span>
                            <span className="text-amber-500 text-xs ml-1.5" data-id="element-941" data-mp="3vmc71u83eb7m">
                              {Math.round(skill.avgAccuracy)}%
                            </span>
                          </div>
                        </li>))}
                  </ul> : <p className="text-amber-500/70 text-xs italic" data-id="element-942" data-mp="2xyr1r1be7ur3">
                    Geen specifieke aandachtspunten.
                  </p>}
              </Card>
            </div>
          </motion.section>}

        {/* 5. Action Plan & Recommendations */}
        {(hasActionPlan || recommendation) && <motion.section {...fadeUp} transition={stagger(4)} className="space-y-3" data-id="element-943" data-mp="3hmgm4nh02sqa">
            <h3 className="flex items-center gap-2 text-lg font-bold text-slate-900 px-0.5" data-id="element-944" data-mp="3lntt50vtx1ys">
              <Lightbulb className="w-4.5 h-4.5 text-slate-500" data-id="element-945" data-mp="3qnerb936d6ag" />
              Advies & Actieplan
            </h3>

            <Card className="p-5 shadow-sm space-y-5" data-id="element-946" data-mp="1rb3vq9695amn">
              {recommendation && <p className="text-slate-700 text-sm font-medium leading-relaxed" data-id="element-947" data-mp="1wa6sz7fzqck2">
                  {recommendation}
                </p>}

              {/* Begin Met */}
              {actionPlan.beginMet && <div className="bg-orange-50 border border-orange-100 rounded-xl p-4 flex flex-col sm:flex-row sm:items-center justify-between gap-3" data-id="element-948" data-mp="1jxl6o1c63vi3">
                  <div className="min-w-0" data-id="element-949" data-mp="1a18e56scznox">
                    <div className="flex items-center gap-1.5 mb-0.5" data-id="element-950" data-mp="1a2jib7ppuxgk">
                      <Zap className="w-3.5 h-3.5 text-orange-500" data-id="element-951" data-mp="27nfkom883q3x" />
                      <span className="text-[10px] font-bold text-orange-700 uppercase tracking-wider" data-id="element-952" data-mp="22pnhgw24ohkf">
                        Prioriteit
                      </span>
                    </div>
                    <div className="font-bold text-slate-900 text-sm" data-id="element-953" data-mp="1topiwtagl298">
                      {actionPlan.beginMet.familyDescription}
                    </div>
                    <div className="text-xs text-slate-500" data-id="element-954" data-mp="mcp3wlpr10u7">
                      {actionPlan.beginMet.moduleTitle} ›{' '}
                      {actionPlan.beginMet.skillName}
                    </div>
                  </div>
                  {onPracticeWeakAreas && <Button onClick={onPracticeWeakAreas} variant="primary" size="sm" className="bg-orange-500 hover:bg-orange-600 whitespace-nowrap flex-shrink-0" data-id="element-955" data-mp="oorcrkrv0rhn">
                      Oefenen
                    </Button>}
                </div>}

              {/* Ga Verder Met */}
              {actionPlan.gaVerderMet.length > 0 && <div data-id="element-956" data-mp="3n7y97d1ovfxq">
                  <h5 className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-2" data-id="element-957" data-mp="3n8hkiawu362s">
                    Daarna verder met
                  </h5>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2" data-id="element-958" data-mp="3e6qfz9e155ai">
                    {actionPlan.gaVerderMet.map((item, idx) => <div key={idx} className="bg-slate-50 border border-slate-200 rounded-lg px-3 py-2.5 flex justify-between items-center" data-id="element-959" data-mp="3i71vy8vg6hfn" data-mpi={idx}>
                        <div className="min-w-0" data-id="element-960" data-mp="2umj4t9edlysf">
                          <div className="font-semibold text-slate-800 text-xs truncate" data-id="element-961" data-mp="38khdpz9xh30j">
                            {item.familyDescription}
                          </div>
                          <div className="text-[10px] text-slate-400" data-id="element-962" data-mp="33nzd9blmrqu0">
                            {item.moduleTitle}
                          </div>
                        </div>
                        {onStartPracticeForModule && <button onClick={() => onStartPracticeForModule(item.moduleId)} className="p-1.5 text-slate-300 hover:text-orange-500 transition-colors flex-shrink-0" aria-label={`Oefen ${item.familyDescription}`} data-id="element-963" data-mp="3rbgyeoae3qo0">
                            <Play className="w-3.5 h-3.5" data-id="element-964" data-mp="17hwowp9hgekh" />
                          </button>}
                      </div>)}
                  </div>
                </div>}

              {/* Uitdaging */}
              {actionPlan.uitdaging && <div className="bg-sky-50 border border-sky-100 rounded-xl p-4" data-id="element-965" data-mp="1lheev7yilxvf">
                  <div className="flex items-center gap-1.5 mb-0.5" data-id="element-966" data-mp="ntnv6asak0iv">
                    <Sparkles className="w-3.5 h-3.5 text-sky-500" data-id="element-967" data-mp="nrb5fbgaklv9" />
                    <span className="text-[10px] font-bold text-sky-700 uppercase tracking-wider" data-id="element-968" data-mp="st8uwnfaccwn">
                      Extra Uitdaging
                    </span>
                  </div>
                  <div className="font-bold text-slate-900 text-sm" data-id="element-969" data-mp="12jbuj6gwwp3y">
                    {actionPlan.uitdaging.suggestion}
                  </div>
                  <div className="text-xs text-slate-500" data-id="element-970" data-mp="5121nx4uwm1g">
                    {actionPlan.uitdaging.moduleTitle}
                  </div>
                </div>}
            </Card>
          </motion.section>}

        {/* 6. Bottom Actions */}
        <motion.section {...fadeUp} transition={stagger(5)} className="pt-4 pb-6 flex flex-col sm:flex-row items-center justify-center gap-3" data-id="element-971" data-mp="3tq2rmvi8tutt">
          {onPracticeWeakAreas && <Button onClick={onPracticeWeakAreas} variant="primary" size="lg" className="w-full sm:w-auto bg-orange-500 hover:bg-orange-600 shadow-sm" data-id="element-972" data-mp="3jqbtmjuh4xoz">
              <BookOpen className="w-4 h-4 mr-2" data-id="element-973" data-mp="35z6s1c7jpvc0" />
              Start met Oefenen
            </Button>}
          {onRetake && <Button onClick={onRetake} variant="outline" size="lg" className="w-full sm:w-auto" data-id="element-974" data-mp="3115baxxle83p">
              Opnieuw maken
            </Button>}
          <Button onClick={onExit} variant="ghost" size="lg" className="w-full sm:w-auto text-slate-500" data-id="element-975" data-mp="2s07g9554tax9">
            Terug naar Dashboard
          </Button>
        </motion.section>
      </main>
    </div>;
}